import { Box, Radio, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useId } from 'react';

import { formatQty } from '../utils/bulkLotNdcStatus';

/** A lot inside this many days of expiry is flagged, without being withheld. */
const EXPIRING_SOON_DAYS = 30;

// Radio · Lot · Expires · Free in this batch, per facility. As Change NDC & lot's table, with a
// wider last column for the facility names.
const LOT_TABLE_COLUMNS = '36px 200px minmax(0, 1fr) 250px';

const LOT_TABLE_HEADER_SX = {
  display: 'grid',
  gridTemplateColumns: LOT_TABLE_COLUMNS,
  columnGap: 1,
  px: 1,
  py: 0.75,
  bgcolor: 'grey.100',
  borderBottom: '1px solid',
  borderColor: 'divider',
  '& > *': {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'text.secondary',
  },
};

/** "85 free at Smartscripts", or why the lot is not on offer there. */
const describeAtFacility = ({ lot, facility }) => {
  const at = lot.atFacilities.find(
    (offered) => offered.facility.fulfillingPharmacyId === facility.fulfillingPharmacyId
  );
  if (at)
    return {
      isOffered: true,
      text: `${formatQty(at.lot.remainingQty)} free at ${facility.facilityName}`,
    };

  return {
    isOffered: false,
    text: lot.stockedFacilityIds.has(facility.fulfillingPharmacyId)
      ? `not enough free at ${facility.facilityName}`
      : `not stocked at ${facility.facilityName}`,
  };
};

/**
 * POM-6537 — Bulk Lot Update's stock lots: one row per lot number and BUD across the NDC's
 * facilities (findMergedBulkLots), each saying what is free of it at every facility. No facility
 * is picked first: each row of the NDC takes its own facility's record of the lot, and the preview
 * names the rows a facility without it leaves out.
 */
const BulkLotUpdateLotTable = ({ lots, facilities, selectedLotKey, onSelectLot }) => {
  const freeTextIdPrefix = useId();

  if (lots.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        {`No lot of this NDC is in stock at ${facilities
          .map((facility) => facility.facilityName)
          .join(' or ')}.`}
      </Typography>
    );
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={LOT_TABLE_HEADER_SX}>
        <span />
        <span>Lot</span>
        <span>Expires</span>
        <Box component='span' sx={{ textAlign: 'right' }}>
          Free in this batch
        </Box>
      </Box>
      {lots.map((lot, index) => {
        const isSelected = lot.key === selectedLotKey;
        const freeTextId = `${freeTextIdPrefix}-free-${index}`;
        const expiringSoon =
          Number(lot.daysUntilExpiry) <= EXPIRING_SOON_DAYS && Number(lot.daysUntilExpiry) >= 0;

        return (
          /* A real label around a real radio, all sharing one name, so the list is a native radio
             group: Tab reaches it, the arrow keys move through it, and Space selects. */
          <Box
            key={lot.key}
            component='label'
            sx={{
              display: 'grid',
              gridTemplateColumns: LOT_TABLE_COLUMNS,
              columnGap: 1,
              alignItems: 'center',
              px: 1,
              py: 0.5,
              cursor: 'pointer',
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-of-type': { borderBottom: 0 },
              bgcolor: (theme) =>
                isSelected ? alpha(theme.palette.success.main, 0.08) : undefined,
              boxShadow: (theme) =>
                isSelected ? `inset 3px 0 0 ${theme.palette.success.main}` : 'none',
              '&:focus-within': { outline: '2px solid', outlineColor: 'primary.main' },
            }}
          >
            <Radio
              checked={isSelected}
              onChange={() => onSelectLot(lot.key)}
              name='bulk-stocked-lot'
              size='small'
              color='success'
              // The free figures are read out with the lot, as they decide which to pick.
              inputProps={{ 'aria-label': lot.lotNumber, 'aria-describedby': freeTextId }}
            />
            <Typography variant='body2' sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {lot.lotNumber}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {String(lot.expirationDate).slice(0, 10)}
              {expiringSoon ? ` · Expiring · ${lot.daysUntilExpiry}d` : ''}
            </Typography>
            <Box id={freeTextId} sx={{ textAlign: 'right', minWidth: 0 }}>
              {facilities.map((facility) => {
                const { isOffered, text } = describeAtFacility({ lot, facility });

                return (
                  <Typography
                    key={facility.fulfillingPharmacyId}
                    variant={isOffered ? 'body2' : 'caption'}
                    component='div'
                    color={isOffered ? 'text.primary' : 'text.secondary'}
                  >
                    {text}
                  </Typography>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

BulkLotUpdateLotTable.propTypes = {
  /** findMergedBulkLots' output, earliest expiry first. */
  lots: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      lotNumber: PropTypes.string,
      atFacilities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      stockedFacilityIds: PropTypes.instanceOf(Set).isRequired,
    })
  ).isRequired,
  /** The choice's facilities, A–Z. */
  facilities: PropTypes.arrayOf(
    PropTypes.shape({ fulfillingPharmacyId: PropTypes.string, facilityName: PropTypes.string })
  ).isRequired,
  selectedLotKey: PropTypes.string,
  onSelectLot: PropTypes.func.isRequired,
};

BulkLotUpdateLotTable.defaultProps = { selectedLotKey: null };

export default BulkLotUpdateLotTable;
